import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import appConfigs from "@/app/appConfigurations";
import { useUserProfileStore } from "@/store/userProfile";
import { authService } from "@/services/serviceProvider";

// Define the structure of a retry queue item
interface RetryQueueItem {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
}

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

const axiosInstance: AxiosInstance = axios.create({
  baseURL: appConfigs.baseUrl,
  headers: {
    "Content-Type": "Application/json",
  },
});

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Attach access token from localStorage (or any other storage mechanism)

    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // Handle error on request
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: AxiosRequestConfig = error.config;
    if (error.response && error.response.status === 401) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Refresh the access token
          const newRefresh = await axios.get(
            `${appConfigs.authApiUrl}/api/auth/refresh-token`,
            {
              withCredentials: true, // Include cookies with the request
            }
          );

          const newAccessToken = newRefresh.data.accessToken;
          const newRefreshToken = newRefresh.data.refreshToken;

          localStorage.setItem("accessToken", newAccessToken);
          await authService.setRefershTokenCookie(newRefreshToken);

          // Update the request headers with the new access token
          error.config.headers["Authorization"] = `Bearer ${newAccessToken}`;

          // Retry all requests in the queue with the new token
          refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
            axiosInstance
              .request(config)
              .then((response) => resolve(response))
              .catch((err) => reject(err));
          });

          // Clear the queue
          refreshAndRetryQueue.length = 0;

          // Retry the original request
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // Handle token refresh error
          await authService.clearRefershTokenCookie();
          localStorage.removeItem("accessToken");

          const stateUserProfile = useUserProfileStore();
          stateUserProfile.clearProfile();
          window.location.href = `${appConfigs.authAppUrl}?clientId=${appConfigs.client_id}`;

          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }

      // Add the original request to the queue
      return new Promise<void>((resolve, reject) => {
        refreshAndRetryQueue.push({ config: originalRequest, resolve, reject });
      });
    } else if (error.response && error.response.status === 403) {
      window.location.href = "/auth/403";
    }

    // Return a Promise rejection if the status code is not 401
    return Promise.reject(error);
  }
);

export default axiosInstance;
